import { useState } from 'react';
import { Tag as TagIcon, X, Plus } from 'lucide-react';

export const CHAR_TYPES = [
  { key: 'main', label: 'Main', color: '#FF4500' },
  { key: 'background', label: 'Background', color: '#00F0FF' },
  { key: 'extra', label: 'Extra', color: '#8A8A93' },
];

export const typeMeta = (type) => CHAR_TYPES.find(t => t.key === (type || 'main')) || CHAR_TYPES[0];

export function TypeBadge({ type }) {
  const m = typeMeta(type);
  return (
    <span
      data-testid={`char-type-badge-${type || 'main'}`}
      className="text-[9px] tracking-[0.12em] uppercase font-bold px-1.5 py-0.5 rounded"
      style={{ background: `${m.color}22`, color: m.color }}
    >
      {m.label}
    </span>
  );
}

export function TypeTagEditor({ type, onType, tags, onTags, allTags = [] }) {
  const [draft, setDraft] = useState('');
  const current = tags || [];

  const addTag = (raw) => {
    const t = (raw || '').trim().replace(/,$/, '').trim();
    if (!t) return;
    if (!current.some(x => x.toLowerCase() === t.toLowerCase())) onTags([...current, t]);
    setDraft('');
  };
  const removeTag = (t) => onTags(current.filter(x => x !== t));

  const suggestions = allTags.filter(t => !current.some(x => x.toLowerCase() === t.toLowerCase()));

  return (
    <div className="space-y-4">
      <div>
        <label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#8A8A93] mb-1.5 block">Character type</label>
        <div className="flex gap-2" data-testid="char-type-selector">
          {CHAR_TYPES.map(t => (
            <button
              key={t.key}
              type="button"
              data-testid={`char-type-${t.key}`}
              onClick={() => onType(t.key)}
              className="flex-1 py-2 rounded-lg text-xs font-semibold border transition-all"
              style={(type || 'main') === t.key
                ? { background: `${t.color}22`, color: t.color, borderColor: `${t.color}66` }
                : { background: 'rgba(255,255,255,0.03)', color: '#8A8A93', borderColor: 'rgba(255,255,255,0.06)' }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#8A8A93] mb-1.5 flex items-center gap-1">
          <TagIcon className="w-3 h-3" /> Tags
        </label>
        {current.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2" data-testid="char-tags-chips">
            {current.map(t => (
              <span key={t} className="flex items-center gap-1 px-2 py-1 rounded-md bg-[#A78BFA]/15 text-[#A78BFA] text-[11px]">
                {t}
                <button type="button" data-testid={`char-tag-remove-${t}`} onClick={() => removeTag(t)} className="hover:text-white"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input
            data-testid="char-tag-input"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(draft); } }}
            className="ff-input flex-1 text-sm py-2"
            placeholder="Type a tag and press Enter (e.g. hero, villain)"
          />
          <button type="button" data-testid="char-tag-add" onClick={() => addTag(draft)}
            className="px-3 rounded-lg bg-white/[0.06] text-[#8A8A93] hover:text-white transition-colors">
            <Plus className="w-4 h-4" />
          </button>
        </div>
        {suggestions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {suggestions.slice(0, 12).map(t => (
              <button key={t} type="button" data-testid={`char-tag-suggest-${t}`} onClick={() => addTag(t)}
                className="px-2 py-0.5 rounded-md bg-white/[0.04] text-[#8A8A93] text-[11px] hover:bg-white/[0.08] hover:text-white transition-colors">
                + {t}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
